import ApiClient from '../../core/ApiClient';
import { normalizeCmsAssetUrl } from '../../utils/cmsAssetUrl.js';
import { normalizeProductsCatalogResponse } from '../../utils/parentsProductCatalog.js';
import { resolveDefaultVehicleCountryCode } from '../../utils/languageDefaultCountry.js';
import {
    readClientId,
    readGclid,
    readMsclkid,
    readSessionId,
    sha256HexEmail,
} from '../../analytics/sendMakePurchaseWebhook.js';
import {
    getCategoryCanonicalDescription,
    getCategoryDescription,
    getCategoryId,
    normalizeDurationKey
} from '../../components/BuyVignette/config/durationKeywords.js';
import {
    fuelTypeOptionSortIndex,
    fuelProductDisplayTitle,
    vignetteFuelRowOrderIndex
} from '../../utils/fuelTypeProduct.js';

export const PAYMENT_METHODS_NEEDING_BILLING_FORM = ['billie', 'in3', 'klarna', 'riverty'];

export const STEP_SELECT_VIGNETTE = 1;
const STEP_NAMES = ['select-vignette', 'enter-details', 'payment'];
const DISALLOWED_FORM_KEYS = new Set(['name']);
const AUTO_SELECTED_PLATE_COUNTRY_KEY = 'autoSelectedPlateCountry';
const APPLIED_LANGUAGE_CODE_KEY = 'cartLangCodeApplied';

/**
 * Effective country for GET /payments/methods/sorted — derived from the user's
 * `plate_country` (the vehicle registration country). Cached here so toggling the
 * dropdown back-and-forth doesn't refetch the same list; reset via
 * `resetPaymentMethodsPlateCache` when locale-affecting state changes (e.g. language).
 */
let paymentMethodsFetchedForPlateKey = null;

function resetPaymentMethodsPlateCache() {
    paymentMethodsFetchedForPlateKey = null;
}

const getDefaultFormData = () => ({
    vehicle_type: '1-day',
    /** Locale-stable vignette duration group; matches product.category.id when API provides it. */
    category_id: null,
    product_id: null,
    selected_duration: null,
    start_date: '',

    plate_country: '',
    license_plate: '',
    license_plate_confirmation: '',
    email: '',
    email_confirmation: '',

    need_an_invoice: false,
    invoice_name: '',
    invoice_surname: '',
    invoice_company_name: '',
    invoice_vat_number: '',
    invoice_city: '',
    invoice_address: '',
    invoice_zipcode: '',
    invoice_country: '',

    payment_method: null,
    terms_condition: false,
    confirm_entered_data: false,

    /** Extra billing fields for BNPL-style gateways (see PAYMENT_METHODS_NEEDING_BILLING_FORM). */
    billing_data: {},
});

const STEP_FIELDS = {
    step1: ['start_date'],
    step2: [
        'product_id',
        'plate_country',
        'license_plate',
        'license_plate_confirmation',
        'email',
        'email_confirmation',
        'need_an_invoice',
        'invoice_name',
        'invoice_surname',
        'invoice_company_name',
        'invoice_vat_number',
        'invoice_city',
        'invoice_address',
        'invoice_zipcode',
        'invoice_country'
    ],
    step3: ['payment_method', 'confirm_entered_data', 'terms_condition']
};

const STEP_TO_API_NUMBER = {
    step1: '1',
    step2: '2',
    step3: '3'
};

/** Keys used by buy-vignette flow; cleared when checkout completes so the next order starts at step 1. */
const BUY_FLOW_LOCAL_STORAGE_KEYS = [
    'vignetteFormData',
    'currentStep',
    'formData',
    'product_id',
];

function getValidationFieldsForStep(state, stepKey) {
    const apiStep = STEP_TO_API_NUMBER[stepKey];
    const apiFields = (state.formFields || [])
        .filter((f) => String(f.step) === String(apiStep))
        .map((f) => f.key)
        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));

    // Prefer server-driven step fields when available.
    if (apiFields.length) return apiFields;
    return (STEP_FIELDS[stepKey] || []).filter((key) => !DISALLOWED_FORM_KEYS.has(key));
}

function normalizeErrorFieldKey(rawKey) {
    const key = String(rawKey || '');
    let base = key;
    if (key.startsWith('form.main.')) base = key.replace('form.main.', '');
    else if (key.startsWith('main.')) base = key.replace('main.', '');
    else if (key.startsWith('form.')) base = key.replace('form.', '');
    else if (key.startsWith('billing_data.')) base = key.replace('billing_data.', '');
    return base;
}

/** Same rules as EnterDetails `categoryProducts` filter (fuel / vignette rows). */
function productBelongsToSelectedCategory(product, formData) {
    const fd = formData || {};
    const cid = fd.category_id;
    if (cid != null && cid !== '') {
        const pid = getCategoryId(product);
        if (pid == null || pid === '') return false;
        return String(pid) === String(cid);
    }
    const vt = String(fd.vehicle_type || '').toLowerCase().trim();
    if (!vt) return false;
    const localized = String(getCategoryDescription(product) || '').toLowerCase().trim();
    if (localized && localized === vt) return true;
    const canon = String(getCategoryCanonicalDescription(product) || '').toLowerCase().trim();
    if (canon && canon === vt) return true;
    const nk = (s) => normalizeDurationKey(s) || String(s || '').toLowerCase().trim();
    return nk(localized) === nk(vt) || nk(canon) === nk(vt);
}

function extractProductDuration(product) {
    const d = Number(product?.duration);
    if (Number.isFinite(d) && d > 0) return d;
    const match = product?.path && String(product.path).match(/(\d+)-day/);
    return match ? parseInt(match[1], 10) : 0;
}

/** Sorted fuel products for current vignette category — mirrors EnterDetails `categoryProducts` order. */
function getSortedCategoryProducts(products, formData) {
    const list = Array.isArray(products) ? products : [];
    if (!list.length) return [];
    const fd = formData || {};
    const hasCategory =
        (fd.category_id != null && fd.category_id !== '')
        || String(fd.vehicle_type || '').trim();
    if (!hasCategory) return [];
    return list
        .filter((p) => productBelongsToSelectedCategory(p, fd))
        .sort((a, b) => {
            const byId = vignetteFuelRowOrderIndex(a) - vignetteFuelRowOrderIndex(b);
            if (byId !== 0) return byId;
            return (
                fuelTypeOptionSortIndex(fuelProductDisplayTitle(a))
                - fuelTypeOptionSortIndex(fuelProductDisplayTitle(b))
            );
        });
}

function normalizeInvoiceVatNumber(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat_number')) return targetForm;
    const sanitizeVat = (value) =>
        String(value ?? '')
            .trim()
            .replace(/\s+/g, '')
            .toUpperCase();
    targetForm.invoice_vat_number = sanitizeVat(targetForm.invoice_vat_number);
    return targetForm;
}

function invoiceVatPayloadPatch(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat_number')) return {};
    return { invoice_vat_number: String(targetForm.invoice_vat_number ?? '') };
}

function extractVatValidationError(responseData) {
    const message = String(responseData?.message ?? '');
    const exception = String(responseData?.exception ?? '');
    const looksLikeVatTypeError =
        exception === 'TypeError'
        && /VatValidator::validate/.test(message)
        && /must be of type string,\s*array given/i.test(message);

    const isViesConcurrentLimitError =
        /ViesException/.test(exception)
        && /MS_MAX_CONCURRENT_REQ/i.test(message);

    if (!looksLikeVatTypeError && !isViesConcurrentLimitError) return null;

    const userMessage = looksLikeVatTypeError
        ? 'Please enter a valid VAT number (e.g. DE129273398).'
        : 'VAT validation service is busy right now. Please try again in 1-2 minutes.';
    const err = new Error(userMessage);
    err.validationErrors = {
        invoice_vat_number: userMessage,
    };
    return err;
}

/**
 * Some /forms/fields responses omit invoice_vat_number. Inject a step-2 text field
 * between business name and the next invoice field so it appears in EnterDetails
 * and is included in API-driven validation keys.
 */
function ensureInvoiceVatField(formFields) {
    if (!Array.isArray(formFields) || formFields.some((f) => f.key === 'invoice_vat_number')) {
        return formFields;
    }
    const step2 = formFields.filter((f) => String(f.step) === '2');
    const sorted = [...step2].sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
    const businessPos = sorted.findIndex((f) => f.key === 'invoice_company_name');
    const business = businessPos >= 0 ? sorted[businessPos] : null;
    const next = businessPos >= 0 ? sorted[businessPos + 1] : null;

    let index = 100;
    if (business && next) {
        const bi = Number(business.index) || 0;
        const ni = Number(next.index) || bi + 10;
        index = bi + (ni - bi) / 2;
    } else if (business) {
        index = Number(business.index) + 5;
    } else {
        const invoiceBlock = sorted.filter((f) => f.key && f.key.startsWith('invoice_'));
        const last = invoiceBlock[invoiceBlock.length - 1];
        if (last) index = Number(last.index) + 5;
    }

    const vatField = {
        key: 'invoice_vat_number',
        type: 'text_input',
        step: '2',
        section_value: 'invoice',
        index,
        translated: {
            label: 'VAT number',
            placeholder: 'e.g. DE129273398',
        },
    };
    return [...formFields, vatField];
}

function pickStepData(formData, fields) {
    const result = {};
    fields.forEach((key) => {
        result[key] = formData[key];
    });
    return result;
};

/** One shared load for ShoppingCart, BuyVignette, VignettePrices, etc. */
let initialDataLoaded = false;
let initialDataLoadPromise = null;

/** Coalesce concurrent POST /orders/amount calls for the same product + payment_method */
const paymentMethodFeeInflight = new Map();

function apiClient() {
    return new ApiClient();
}

export function normalizeSiteLanguageCode(langCode) {
    const raw = String(langCode || '').toLowerCase().trim();
    if (raw === 'ro-md' || raw === 'moldova-ro' || raw === 'ro_md') {
        return 'md';
    }
    return raw;
}

function getStoredAppliedLanguageCode() {
    try {
        if (typeof localStorage === 'undefined') return '';
        return normalizeSiteLanguageCode(localStorage.getItem(APPLIED_LANGUAGE_CODE_KEY) || '');
    } catch (e) {
        return '';
    }
}

/**
 * A hand-picked plate country stays only while the site language stays the same.
 * Auto-selected countries follow the language. A language change replaces either one.
 */
export function shouldKeepSavedPlateCountry(savedCountry, langCode) {
    const current = String(savedCountry || '').trim();
    if (!current) return false;

    const autoSelected = getStoredAutoSelectedPlateCountry();
    const manual = !autoSelected || autoSelected !== current;
    if (!manual) return false;

    const applied = getStoredAppliedLanguageCode();
    const normalizedLang = normalizeSiteLanguageCode(langCode);
    // Legacy carts without cartLangCodeApplied must realign with the current UI language.
    const languageChanged = !applied || applied !== normalizedLang;
    return !languageChanged;
}

function getStoredAutoSelectedPlateCountry() {
    try {
        if (typeof localStorage === 'undefined') return '';
        return String(localStorage.getItem(AUTO_SELECTED_PLATE_COUNTRY_KEY) || '').trim();
    } catch (e) {
        return '';
    }
}

function setStoredAutoSelectedPlateCountry(code) {
    try {
        if (typeof localStorage === 'undefined') return;
        const normalized = String(code || '').trim();
        if (normalized) {
            localStorage.setItem(AUTO_SELECTED_PLATE_COUNTRY_KEY, normalized);
        } else {
            localStorage.removeItem(AUTO_SELECTED_PLATE_COUNTRY_KEY);
        }
    } catch (e) {}
}

function clearStoredAutoSelectedPlateCountry() {
    setStoredAutoSelectedPlateCountry('');
}

function apiRequest(path, options = {}) {
    return apiClient().request({ path, ...options }).then((res) => res?.data);
}

async function withLoading(commit, callback) {
    commit('INCREMENT_LOADING');
    try {
        return await callback();
    } finally {
        commit('DECREMENT_LOADING');
    }
}

function normalizeCountriesResponse(responseData) {
    return (responseData?.data || []).map(c => ({
        id: c.id ?? c.uuid ?? null,
        code: c.code,
        dial_code: c.dial_code,
        flag: normalizeCmsAssetUrl(c.flag) || '',
        translated: { name: c.translated?.name || c.code }
    }));
}

function normalizeFormFieldsResponse(responseData, countries) {
    return ensureInvoiceVatField((responseData?.data || []).map(field => {
        const f = { ...field };
        if (['plate_country', 'invoice_country'].includes(f.key)) {
            f.options = countries || [];
        }
        return f;
    }));
}

function getAllowedFormKeys(formFields) {
    const apiKeys = formFields
        .map(f => f.key)
        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));
    const systemKeys = Object.keys(getDefaultFormData())
        .filter(key => !apiKeys.includes(key));

    /** Always allow plate country (API field list can omit it; createOrder must send it). */
    return [...new Set([...apiKeys, ...systemKeys, 'plate_country'])];
}

function normalizePaymentMethodImage(method) {
    // Keep the API-provided image URL exactly as-is (no local-logo remapping
    // and no CMS host rewrite); the Payment UI renders method.image directly.
    return method;
}

function normalizeSortedPaymentMethodsResponse(responseData) {
    const data = responseData?.data;
    const main = Array.isArray(data?.main) ? data.main.map(normalizePaymentMethodImage) : [];
    const other = Array.isArray(data?.other) ? data.other.map(normalizePaymentMethodImage) : [];
    return { main, other };
}

async function loadCountriesData(commit, languageId) {
    const responseData = await apiRequest('/countries');
    const countries = normalizeCountriesResponse(responseData);
    commit('SET_COUNTRIES', countries);
    commit('SET_COUNTRIES_FETCH_LANGUAGE_ID', languageId ?? null);
    return countries;
}

/** Concurrent callers share one in-flight GET /products/parents (home GetProducts + buy flow initial load). */
let parentsCatalogFetchInflight = null;
let parentsCatalogFetchKey = null;

function buildProductsCatalogPath(currencyCode) {
    const code = String(currencyCode || '').trim().toUpperCase();
    if (!code) {
        return '/products';
    }

    return `/products?currency=${encodeURIComponent(code)}`;
}

/** Single CMS call: GET /products?currency=… → flat products + category buckets. */
async function loadParentsCatalogData(commit, options = {}) {
    const { currency = '', force = false } = options;
    const path = buildProductsCatalogPath(currency);
    const fetchKey = path;

    if (!force && parentsCatalogFetchInflight && parentsCatalogFetchKey === fetchKey) {
        return parentsCatalogFetchInflight;
    }

    parentsCatalogFetchKey = fetchKey;
    parentsCatalogFetchInflight = (async () => {
        const responseData = await apiRequest(path);
        const { categorizedProducts, products, productIconItems } = normalizeProductsCatalogResponse(responseData);
        commit('SET_CATEGORIZED_PRODUCTS', categorizedProducts);
        commit('SET_PRODUCTS', products);
        commit('SET_PRODUCTS_CURRENCY_CODE', currency);
        commit('SET_PRODUCT_ICON_ITEMS', productIconItems);
        return products;
    })().finally(() => {
        parentsCatalogFetchInflight = null;
    });

    return parentsCatalogFetchInflight;
}

async function loadFormFieldsData(commit, countries) {
    const responseData = await apiRequest('/forms/fields');
    const formFields = normalizeFormFieldsResponse(responseData, countries);
    commit('SET_FORM_FIELDS', formFields);
    commit('SET_ALLOWED_FORM_KEYS', getAllowedFormKeys(formFields));
    return formFields;
}

async function loadPaymentMethodsData(commit, plateCountry) {
    // Effective payer country comes from the vehicle registration (plate_country).
    // Falls back to 'CZ' so the call still succeeds before the user picks a country.
    const effectiveKey = String(plateCountry || 'CZ').toUpperCase();
    const responseData = await apiRequest(`/payments/methods/sorted?country=${encodeURIComponent(effectiveKey)}`, {
        data: { country: effectiveKey },
    });
    const { main, other } = normalizeSortedPaymentMethodsResponse(responseData);
    commit('SET_PAYMENT_METHODS_SORTED', { main, other });
    paymentMethodsFetchedForPlateKey = effectiveKey;
    return [...main, ...other];
}

/**
 * Homepage already fetched this catalog. Keep it for the next full navigation
 * (cart → buy-vignette) so step 1 can paint without waiting on the same APIs.
 */
const CHECKOUT_CATALOG_CACHE_KEY = 'cv_checkout_catalog_v1';
const CHECKOUT_CATALOG_TTL_MS = 15 * 60 * 1000;
const CHECKOUT_CATALOG_FRESH_MS = 60 * 1000;

function checkoutCatalogCacheIdentity(langId, currencyCode) {
    return `${String(langId ?? '')}|${String(currencyCode || '').trim().toUpperCase()}`;
}

function currentPlateKey(formData) {
    return String(formData?.plate_country || 'CZ').trim().toUpperCase() || 'CZ';
}

function persistCheckoutCatalog(state, langId, currencyCode) {
    if (typeof sessionStorage === 'undefined') return;
    if (!Array.isArray(state?.products) || !state.products.length) return;
    if (!Array.isArray(state.formFields) || !state.formFields.length) return;
    try {
        sessionStorage.setItem(CHECKOUT_CATALOG_CACHE_KEY, JSON.stringify({
            v: 1,
            key: checkoutCatalogCacheIdentity(langId, currencyCode),
            savedAt: Date.now(),
            products: state.products,
            categorizedProducts: state.categorizedProducts,
            productIconItems: state.productIconItems,
            productsCurrencyCode: state.productsCurrencyCode,
            formFields: state.formFields,
            allowedFormKeys: state.allowedFormKeys,
            paymentMethodsMain: state.paymentMethodsMain,
            paymentMethodsOther: state.paymentMethodsOther,
            paymentMethodsPlateKey: paymentMethodsFetchedForPlateKey,
        }));
    } catch (e) {}
}

function readCheckoutCatalog(langId, currencyCode) {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const parsed = JSON.parse(sessionStorage.getItem(CHECKOUT_CATALOG_CACHE_KEY) || 'null');
        if (!parsed || parsed.v !== 1) return null;
        if (parsed.key !== checkoutCatalogCacheIdentity(langId, currencyCode)) return null;
        if (!Array.isArray(parsed.products) || !parsed.products.length) return null;
        const age = Date.now() - Number(parsed.savedAt);
        if (!Number.isFinite(age) || age > CHECKOUT_CATALOG_TTL_MS) return null;
        return parsed;
    } catch (e) {
        return null;
    }
}

function applyCheckoutCatalog(commit, state, cached) {
    commit('SET_CATEGORIZED_PRODUCTS', cached.categorizedProducts);
    commit('SET_PRODUCTS', cached.products);
    commit('SET_PRODUCTS_CURRENCY_CODE', cached.productsCurrencyCode);
    commit('SET_PRODUCT_ICON_ITEMS', cached.productIconItems);
    if (Array.isArray(cached.formFields) && cached.formFields.length) {
        commit('SET_FORM_FIELDS', cached.formFields);
        commit('SET_ALLOWED_FORM_KEYS', Array.isArray(cached.allowedFormKeys) ? cached.allowedFormKeys : []);
    }
    const plate = currentPlateKey(state.formData);
    const hasMethods = (cached.paymentMethodsMain?.length || cached.paymentMethodsOther?.length);
    if (hasMethods && cached.paymentMethodsPlateKey === plate) {
        commit('SET_PAYMENT_METHODS_SORTED', {
            main: cached.paymentMethodsMain,
            other: cached.paymentMethodsOther,
        });
        paymentMethodsFetchedForPlateKey = plate;
    }
}

function revalidateCheckoutCatalog(ctx, { refreshCatalog = true, refreshPayments = false } = {}) {
    const { commit, state, rootState, rootGetters } = ctx;
    const langId = rootState.lang?.id ?? null;
    const currencyCode = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase();
    const persist = () => persistCheckoutCatalog(state, langId, currencyCode);

    if (refreshCatalog) {
        loadParentsCatalogData(commit, { currency: currencyCode, force: true }).then(persist).catch(() => {});
        if (state.countries?.length) {
            loadFormFieldsData(commit, state.countries).then(persist).catch(() => {});
        }
    }
    if (refreshPayments || paymentMethodsFetchedForPlateKey !== currentPlateKey(state.formData)) {
        loadPaymentMethodsData(commit, state.formData?.plate_country).then(persist).catch(() => {});
    }
}

function getNormalizedLanguageCode(rootState) {
    return normalizeSiteLanguageCode(rootState?.lang?.code);
}

function parseSavedFormData(savedData) {
    if (!savedData) return null;

    const parsed = JSON.parse(savedData);
    const sanitizedParsed = Object.fromEntries(
        Object.entries(parsed || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
    );
    const merged = {
        ...getDefaultFormData(),
        ...sanitizedParsed,
    };

    const today = new Date().toISOString().slice(0, 10);

    if (merged.start_date && merged.start_date < today) {
        merged.start_date = today;
    }

    if (!merged.start_date) {
        merged.start_date = today;
    }

    if (typeof merged.invoice_vat_number !== 'string') {
        merged.invoice_vat_number = String(merged.invoice_vat_number ?? '');
    }

    if (!merged.billing_data || typeof merged.billing_data !== 'object' || Array.isArray(merged.billing_data)) {
        merged.billing_data = {};
    }

    return merged;
}

function getDefaultProductPatch(products, formData) {
    if (!Array.isArray(products) || !products.length) return null;

    const sorted = getSortedCategoryProducts(products, formData);
    const rawId = formData?.product_id;
    const hasId = rawId != null && rawId !== '';
    const stillListed = hasId && products.some(
        (p) => p.id === rawId || String(p.id) === String(rawId)
    );
    if (stillListed) return null;

    if (!sorted.length) {
        return hasId
            ? { product_id: null, selected_duration: null }
            : null;
    }

    const first = sorted[0];
    const duration = extractProductDuration(first);
    const vehicle_type = getCategoryDescription(first);
    const catId = getCategoryId(first);
    const patch = {
        product_id: first.id,
        selected_duration: duration || null,
        category_id: catId != null && catId !== '' ? catId : formData.category_id
    };
    if (vehicle_type) patch.vehicle_type = vehicle_type;
    return patch;
}

function getStepValidationFields(state, stepKey) {
    const fields = getValidationFieldsForStep(state, stepKey);

    if (!fields) {
        throw new Error(`Unknown step: ${stepKey}`);
    }

    let normalizedFields = [...new Set(fields)];

    if (stepKey === 'step2' && !normalizedFields.includes('invoice_vat_number')) {
        normalizedFields = [...normalizedFields, 'invoice_vat_number'];
    }
    if (stepKey === 'step2' && !normalizedFields.includes('plate_country')) {
        normalizedFields = [...normalizedFields, 'plate_country'];
    }

    return normalizedFields;
}

function prepareValidationPayload(state, stepKey) {
    const fields = getStepValidationFields(state, stepKey);
    const stepForm = pickStepData(state.formData, fields);
    const filteredStepForm = Object.keys(stepForm)
        .filter(key => state.allowedFormKeys.includes(key))
        .filter(key => !DISALLOWED_FORM_KEYS.has(key))
        .reduce((obj, key) => {
            obj[key] = stepForm[key];
            return obj;
        }, {});

    if (stepKey === 'step2') {
        filteredStepForm.plate_country =
            state.formData.plate_country
            ?? filteredStepForm.plate_country
            ?? '';
    }

    if (stepKey === 'step2' && !filteredStepForm.need_an_invoice) {
        delete filteredStepForm.invoice_name;
        delete filteredStepForm.invoice_surname;
        delete filteredStepForm.invoice_company_name;
        delete filteredStepForm.invoice_vat_number;
        delete filteredStepForm.invoice_city;
        delete filteredStepForm.invoice_address;
        delete filteredStepForm.invoice_zipcode;
        delete filteredStepForm.invoice_country;
    }

    const normalizedStepForm = normalizeInvoiceVatNumber({ ...filteredStepForm });

    return {
        step: stepKey,
        form: {
            main: {
                ...normalizedStepForm,
                ...invoiceVatPayloadPatch(normalizedStepForm),
            }
        }
    };
}

function normalizeValidationResponse(responseData) {
    if (responseData?.errors && Object.keys(responseData.errors).length) {
        const validationErrors = {};

        Object.keys(responseData.errors).forEach((key) => {
            const fieldKey = normalizeErrorFieldKey(key);
            validationErrors[fieldKey] = true;
        });

        const err = new Error(responseData.message || 'Validation failed');
        err.validationErrors = validationErrors;
        throw err;
    }

    const vatTypeErr = extractVatValidationError(responseData);
    if (vatTypeErr) {
        throw vatTypeErr;
    }

    if (responseData?.exception) {
        throw new Error(responseData?.message || 'Validation failed');
    }

    return responseData;
}

/**
 * Turns Axios failures from POST /orders/make into a thrown Error (validationErrors preserved when Laravel sends errors).
 */
function throwNormalizedOrdersMakeError(error) {
    const status = error?.response?.status;
    const raw = error?.response?.data;

    if (raw !== undefined && raw !== null) {
        if (typeof raw === 'string') {
            const plain = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            const snippet = plain.length > 180 ? `${plain.slice(0, 177)}…` : plain;
            const msg = snippet || (status ? `Request failed (${status})` : 'Something went wrong');
            const err = new Error(msg);
            err.responseStatus = status;
            throw err;
        }

        try {
            normalizeValidationResponse(raw);
        } catch (e) {
            if (status != null && e && typeof e === 'object') {
                e.responseStatus = e.responseStatus ?? status;
            }
            throw e;
        }

        const msg =
            (typeof raw.message === 'string' && raw.message.trim())
            || (status ? `Request failed (${status})` : 'Something went wrong');
        const err = new Error(msg);
        err.responseStatus = status;
        throw err;
    }

    const fallback =
        (typeof error?.message === 'string' && error.message.trim())
        || 'Unable to complete order. Please try again.';
    const err = new Error(fallback);
    err.responseStatus = status;
    throw err;
}

function mapSettledResults(keys, results) {
    return keys.reduce((acc, key, index) => {
        const result = results[index];
        if (result.status === 'fulfilled') {
            acc.data[key] = result.value;
        } else {
            acc.errors[key] = result.reason;
        }
        return acc;
    }, { data: {}, errors: {} });
}

function createPartialLoadError(errors) {
    const firstError = Object.values(errors)[0];
    const error = firstError instanceof Error
        ? firstError
        : new Error('Initial data load failed');
    error.partialErrors = errors;
    return error;
}

function buildBillingPayloadForOrder(formData, billingFormFields) {
    const fields = Array.isArray(billingFormFields) ? billingFormFields : [];
    const src = formData?.billing_data && typeof formData.billing_data === 'object' ? formData.billing_data : {};
    if (!fields.length) return null;
    const out = {};
    for (const f of fields) {
        const k = f?.key;
        if (!k) continue;
        const raw = src[k];
        out[k] = raw === undefined || raw === null ? '' : (typeof raw === 'string' ? raw.trim() : raw);
    }
    return out;
}

async function buildOrderApiTags(email) {
    return {
        gclid: readGclid(),
        msclkid: readMsclkid(),
        sid: readSessionId(),
        cid: readClientId(),
        site: 'czechvignette.cz',
        /** SHA-256 hex of trimmed email; same algorithm as Make.com purchase webhook. */
        email: await sha256HexEmail(email),
    };
}

export const formModule = {
    namespaced: true,

    state: {
        formData: getDefaultFormData(),
        step: STEP_SELECT_VIGNETTE,
        countries: [],
        products: [],
        /** Currency code used for the last successful GET /products/parents fetch. */
        productsCurrencyCode: null,
        formFields: [],
        paymentMethodsMain: [],
        paymentMethodsOther: [],
        paymentMethodFee: null,
        loadingCount: 0,
        orderData: null,
        allowedFormKeys: [],
        /** From GET /products/parents payload `items` when present — fuel icons per product_id */
        productIconItems: [],
        /** Raw parent rows from GET /products/parents — ShoppingCart / PricingSection buckets. */
        categorizedProducts: [],
        /** Dynamic billing inputs from GET /payments/billing-form (Klarna, Billie, …). */
        billingFormFields: [],
        /** root `lang.id` used for last successful /countries fetch (avoids stale list after language change). */
        countriesFetchLanguageId: null,
        /**
         * Validation errors that must be highlighted on a step the user is not on yet.
         * Set when a later step (e.g. Payment) fails validation for an earlier-step field
         * (e.g. an empty license_plate after a language switch) and we send the user back.
         * Each step reads and clears the keys that belong to it.
         */
        pendingValidationErrors: {},
    },

    mutations: {
        SET_FORM_DATA(state, payload) {
            const today = new Date().toISOString().slice(0, 10);

            const sanitizedPayload = Object.fromEntries(
                Object.entries(payload || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
            );

            if (sanitizedPayload.start_date && sanitizedPayload.start_date < today) {
                sanitizedPayload.start_date = today;
            }

            if (!sanitizedPayload.start_date && !state.formData.start_date) {
                sanitizedPayload.start_date = today;
            }

            state.formData = {
                ...state.formData,
                ...sanitizedPayload
            };
        },
        SET_ALLOWED_FORM_KEYS(state, keys) {
            state.allowedFormKeys = keys;
        },
        INCREMENT_LOADING(state) {
            state.loadingCount += 1;
        },
        DECREMENT_LOADING(state) {
            state.loadingCount = Math.max(0, state.loadingCount - 1);
        },
        CLEAR_FORM_DATA(state) {
            try {
                BUY_FLOW_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
                localStorage.removeItem(AUTO_SELECTED_PLATE_COUNTRY_KEY);
                sessionStorage.removeItem('cv_buy_vignette_step1_user_category');
            } catch (e) {}
            resetPaymentMethodsPlateCache();
            state.formData = getDefaultFormData();
            state.billingFormFields = [];
            state.step = STEP_SELECT_VIGNETTE;
        },

        SET_STEP(state, payload) {
            const n = typeof payload === 'number' ? payload : (STEP_NAMES.indexOf(payload) + 1) || STEP_SELECT_VIGNETTE;
            state.step = n >= 1 && n <= 3 ? n : STEP_SELECT_VIGNETTE;
        },

        SET_COUNTRIES(state, countries) {
            state.countries = Array.isArray(countries)
                ? countries.map((c) => {
                    if (!c || typeof c !== 'object' || !c.flag) return c;
                    return { ...c, flag: normalizeCmsAssetUrl(c.flag) || c.flag || '' };
                })
                : [];
        },

        SET_COUNTRIES_FETCH_LANGUAGE_ID(state, languageId) {
            state.countriesFetchLanguageId =
                languageId === undefined || languageId === null || languageId === ''
                    ? null
                    : languageId;
        },

        SET_PRODUCTS(state, products) {
            state.products = products;
        },

        SET_PRODUCTS_CURRENCY_CODE(state, code) {
            const normalized = String(code ?? '').trim().toUpperCase();
            state.productsCurrencyCode = normalized || null;
        },

        SET_PRODUCT_ICON_ITEMS(state, items) {
            state.productIconItems = Array.isArray(items) ? items : [];
        },

        SET_CATEGORIZED_PRODUCTS(state, rows) {
            state.categorizedProducts = Array.isArray(rows) ? rows : [];
        },

        SET_FORM_FIELDS(state, formFields) {
            state.formFields = formFields;
        },

        SET_PAYMENT_METHODS_SORTED(state, { main, other }) {
            state.paymentMethodsMain = Array.isArray(main) ? main : [];
            state.paymentMethodsOther = Array.isArray(other) ? other : [];
        },

        SET_BILLING_FORM_FIELDS(state, fields) {
            state.billingFormFields = Array.isArray(fields) ? fields : [];
        },

        SET_PENDING_VALIDATION_ERRORS(state, errors) {
            state.pendingValidationErrors =
                errors && typeof errors === 'object' ? { ...errors } : {};
        },

        CLEAR_PENDING_VALIDATION_ERRORS(state) {
            state.pendingValidationErrors = {};
        },

        MERGE_BILLING_DATA(state, patch) {
            const prev = state.formData.billing_data && typeof state.formData.billing_data === 'object'
                ? state.formData.billing_data
                : {};
            state.formData = {
                ...state.formData,
                billing_data: { ...prev, ...(patch || {}) },
            };
        },

        RESET_BILLING_DATA(state) {
            state.formData = {
                ...state.formData,
                billing_data: {},
            };
        },

        SET_PAYMENT_METHOD_FEE(state, fee) {
            state.paymentMethodFee = fee;
        },

        SET_ORDER_DATA(state, payload) {
            state.orderData = payload;
        }
    },

    actions: {
        /**
         * Align license-plate country with the current site language (see LANGUAGE_TO_COUNTRY).
         * A language change always selects the matching plate country and clears the plate number.
         * A hand-picked country is kept only until the language changes.
         * @param {{ force?: boolean }} [options] force replaces a hand-picked country on the same language.
         */
        async syncVehicleCountryToLanguage({ state, commit, rootState, dispatch }, options = {}) {
            const countries = state.countries ?? [];
            if (!countries.length) return state.formData;

            const langCode = getNormalizedLanguageCode(rootState);
            const targetCountry = resolveDefaultVehicleCountryCode(langCode, countries);
            if (!targetCountry) return state.formData;

            const current = String(state.formData?.plate_country || '').trim();
            const force = options && options.force === true;

            if (current === targetCountry) {
                setStoredAutoSelectedPlateCountry(targetCountry);
                await dispatch('saveAppliedLanguageCode', langCode);
                return state.formData;
            }

            if (!force && shouldKeepSavedPlateCountry(current, langCode)) {
                await dispatch('saveAppliedLanguageCode', langCode);
                return state.formData;
            }

            commit('SET_FORM_DATA', {
                plate_country: targetCountry,
                license_plate: '',
                license_plate_confirmation: ''
            });
            setStoredAutoSelectedPlateCountry(targetCountry);
            await dispatch('saveAppliedLanguageCode', langCode);
            await dispatch('saveDataToLocalStorage');
            return state.formData;
        },

        async markPlateCountryManual({ dispatch, rootState }) {
            clearStoredAutoSelectedPlateCountry();
            await dispatch(
                'saveAppliedLanguageCode',
                getNormalizedLanguageCode(rootState)
            );
            await dispatch('saveDataToLocalStorage');
        },

        /**
         * After SSR bootstrap or language navigation: align plate country when the
         * stored UI language differs from the active one (see cartLangCodeApplied).
         */
        async alignPlateCountryWithSiteLanguage({ dispatch, rootState }) {
            const applied = getStoredAppliedLanguageCode();
            const current = getNormalizedLanguageCode(rootState);
            const force = !applied || applied !== current;
            return dispatch('syncVehicleCountryToLanguage', { force });
        },

        async loadSavedData({ commit, state }) {
            const savedData = localStorage.getItem("vignetteFormData");
            const parsed = parseSavedFormData(savedData);

            if (!parsed) return state.formData;

            commit("SET_FORM_DATA", parsed);
            return parsed;
        },

        async saveDataToLocalStorage({ state }) {
            localStorage.setItem("vignetteFormData", JSON.stringify(state.formData));
            return state.formData;
        },

        async saveAppliedLanguageCode(context, langCode) {
            const normalizedLang = normalizeSiteLanguageCode(langCode);
            if (normalizedLang) {
                localStorage.setItem(APPLIED_LANGUAGE_CODE_KEY, normalizedLang);
            }
            return normalizedLang;
        },

        /**
         * Single source of truth for default fuel/vignette row product_id (EnterDetails fuel list).
         * Mirrors EnterDetails categoryProducts sort; does not override a valid existing product_id
         * (including from localStorage). Clears stale ids that no longer match the category.
         */
        async autoSelectDefaultProduct({ state, commit, dispatch }) {
            const products = state.products ?? [];
            const patch = getDefaultProductPatch(products, state.formData);
            if (!patch) return null;

            commit('SET_FORM_DATA', patch);
            await dispatch('saveDataToLocalStorage');
            return state.formData;
        },

        async validateStep({ state, commit }, stepKey) {
            return await withLoading(commit, async () => {
                const responseData = await apiRequest('/forms/validate', {
                    method: 'POST',
                    data: prepareValidationPayload(state, stepKey)
                });

                return normalizeValidationResponse(responseData);
            });
        },

        async createOrder({ state, commit, rootGetters }) {
            return await withLoading(commit, async () => {
                const formData = state.formData;
                const { product_id, payment_method, need_an_invoice } = formData;

                const defaults = getDefaultFormData();
                let cleanForm = state.allowedFormKeys
                    .filter(key => !DISALLOWED_FORM_KEYS.has(key))
                    .reduce((obj, key) => {
                        let val = formData[key];
                        if (val === undefined && Object.prototype.hasOwnProperty.call(defaults, key)) {
                            val = defaults[key];
                        }
                        if (val !== undefined) {
                            obj[key] = val;
                        }
                        return obj;
                    }, {});
                cleanForm.plate_country =
                    formData.plate_country ?? cleanForm.plate_country ?? '';

                if (cleanForm.invoice_company_name !== undefined && cleanForm.invoice_company_name === undefined) {
                    cleanForm.invoice_company_name = cleanForm.invoice_company_name;
                }

                if (!need_an_invoice) {
                    delete cleanForm.invoice_name;
                    delete cleanForm.invoice_surname;
                    delete cleanForm.invoice_company_name;
                    delete cleanForm.invoice_vat_number;
                    delete cleanForm.invoice_city;
                    delete cleanForm.invoice_address;
                    delete cleanForm.invoice_zipcode;
                    delete cleanForm.invoice_country;
                }
                normalizeInvoiceVatNumber(cleanForm);

                delete cleanForm.phone;
                delete cleanForm.billing_data;

                const plate = cleanForm.plate_country ?? '';
                const mainPayload = {
                    ...cleanForm,
                    plate_country: plate,
                    ...invoiceVatPayloadPatch(cleanForm),
                    need_an_invoice,
                };
                delete mainPayload.billing_data;

                const currency = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase()
                    || 'EUR';

                const pmKey = String(payment_method || '').toLowerCase();
                const billingPayload =
                    PAYMENT_METHODS_NEEDING_BILLING_FORM.includes(pmKey) && state.billingFormFields?.length
                        ? buildBillingPayloadForOrder(formData, state.billingFormFields)
                        : null;

                const orderData = {
                    products: [{ id: product_id, quantity: 1 }],
                    form: {
                        main: mainPayload,
                    },
                    currency,
                    ...(billingPayload ? { billing_data: billingPayload } : {}),
                    payment_method,
                    api_tags: await buildOrderApiTags(formData.email),
                };

                let responseData;
                try {
                    responseData = await apiRequest('/orders/make', {
                        method: 'POST',
                        data: orderData
                    });
                } catch (error) {
                    throwNormalizedOrdersMakeError(error);
                }

                /*
                 * The `/api` proxy returns upstream validation failures as an
                 * HTTP 200 whose body still carries `errors`/`exception`, so a
                 * thrown axios error never fires. Run the body through the same
                 * normalizer as validateStep to surface those (e.g. an empty
                 * license_plate) as a rejected promise with `validationErrors`.
                 */
                const normalized = normalizeValidationResponse(responseData);
                commit('SET_ORDER_DATA', normalized);
                return normalized;
            });
        },

        async fetchPaymentMethodFee({ state, commit }) {
            const productId = state.formData?.product_id;
            const paymentMethod = state.formData?.payment_method;

            if (!productId || !paymentMethod) {
                commit('SET_PAYMENT_METHOD_FEE', null);
                return null;
            }

            const key = `${String(productId)}\u0000${String(paymentMethod)}`;
            const inflight = paymentMethodFeeInflight.get(key);
            if (inflight) {
                return inflight;
            }

            const promise = (async () => {
                try {
                    return await withLoading(commit, async () => {
                        const pid = productId;
                        const pm = paymentMethod;

                        commit('SET_PAYMENT_METHOD_FEE', null);

                        const responseData = await apiRequest('/orders/amount', {
                            method: 'POST',
                            data: {
                                products: [{ id: pid, quantity: 1 }],
                                payment_method: pm,
                            },
                        });

                        if (
                            String(state.formData?.product_id ?? '') !== String(pid ?? '')
                            || state.formData?.payment_method !== pm
                        ) {
                            return null;
                        }

                        commit('SET_PAYMENT_METHOD_FEE', responseData?.data?.payment_method_fee ?? null);
                        return responseData?.data ?? null;
                    });
                } finally {
                    paymentMethodFeeInflight.delete(key);
                }
            })();

            paymentMethodFeeInflight.set(key, promise);
            return promise;
        },

        /**
         * Refetch GET /countries (via POST /api) using current language id for translated names.
         */
        async reloadCountries({ commit, dispatch, rootState }) {
            return await withLoading(commit, async () => {
                const countries = await loadCountriesData(commit, rootState.lang?.id ?? null);
                await dispatch('alignPlateCountryWithSiteLanguage');
                return countries;
            });
        },

        /**
         * Loads countries, products, form fields, payment methods once per session
         * (deduped across all components). Pass { force: true } to refetch.
         */
        async loadInitialData({ commit, state, dispatch, rootState, rootGetters }, options = {}) {
            const force = options && options.force === true;
            /*
             * `skipAutoSelect: true` is set by BuyVignette when the page URL
             * carries `?token=…` — we'll fully prefill formData (product,
             * vehicle_type, duration, …) from POST /orders/request-data right
             * after, so picking a default product here is pure wasted work and
             * causes the form to flash a different product on mount.
             */
            const skipAutoSelect = !!(options && options.skipAutoSelect);
            if (force) {
                initialDataLoaded = false;
                initialDataLoadPromise = null;
            }
            if (!force && initialDataLoaded) {
                const currentLangId = rootState.lang?.id;
                const fetchedFor = state.countriesFetchLanguageId;
                const hasCountries = state.countries && state.countries.length;
                const langMismatch =
                    hasCountries
                    && currentLangId != null
                    && (fetchedFor == null
                        || String(fetchedFor) !== String(currentLangId));
                if (langMismatch) {
                    const countries = await dispatch('reloadCountries');
                    await dispatch('reloadProductsCatalog');
                    return { countries, syncedFormData: state.formData };
                }
                const syncedFormData = await dispatch('syncVehicleCountryToLanguage');
                return { syncedFormData };
            }
            if (!force && initialDataLoadPromise) {
                return initialDataLoadPromise;
            }

            const currencyCode = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase();
            if (!force && Array.isArray(state.countries) && state.countries.length) {
                const cached = readCheckoutCatalog(rootState.lang?.id, currencyCode);
                if (cached) {
                    applyCheckoutCatalog(commit, state, cached);
                    initialDataLoaded = true;
                    if (!skipAutoSelect) {
                        await dispatch('autoSelectDefaultProduct');
                    }
                    const syncedFormData = await dispatch('syncVehicleCountryToLanguage');
                    const age = Date.now() - Number(cached.savedAt);
                    const plateMismatch = cached.paymentMethodsPlateKey !== currentPlateKey(state.formData);
                    const stale = age > CHECKOUT_CATALOG_FRESH_MS;
                    if (stale || plateMismatch) {
                        revalidateCheckoutCatalog(
                            { commit, state, rootState, rootGetters },
                            { refreshCatalog: stale, refreshPayments: stale || plateMismatch }
                        );
                    }
                    return { syncedFormData, fromCache: true };
                }
            }

            const run = async () => {
                const langIdForCountries = rootState.lang?.id ?? null;
                /*
                 * Skip /countries when the SSR bootstrap already seeded them for
                 * the current language (see store/index.js → initState +
                 * window.__APP_DATA__.countries). The list is refetched on
                 * language change via reloadCountries(), so the in-memory copy
                 * stays in sync without a duplicate request on initial load.
                 */
                const hasFreshSeededCountries =
                    Array.isArray(state.countries)
                    && state.countries.length > 0
                    && state.countriesFetchLanguageId != null
                    && String(state.countriesFetchLanguageId) === String(langIdForCountries ?? '');

                const countriesSettled = hasFreshSeededCountries
                    ? [{ status: 'fulfilled', value: state.countries }]
                    : await Promise.allSettled([
                        loadCountriesData(commit, langIdForCountries)
                    ]);
                const countriesResult = mapSettledResults(['countries'], countriesSettled);
                const countries = countriesResult.data.countries
                    || state.countries
                    || [];

                if (!countries.length) {
                    throw new Error('Countries are required before formFields load');
                }

                await dispatch('syncVehicleCountryToLanguage');

                const keys = ['products', 'formFields', 'paymentMethods'];
                const settled = await Promise.allSettled([
                    loadParentsCatalogData(commit, { currency: currencyCode }),
                    loadFormFieldsData(commit, countries),
                    loadPaymentMethodsData(commit, state.formData?.plate_country)
                ]);
                const result = mapSettledResults(keys, settled);
                const partialResult = {
                    data: {
                        ...countriesResult.data,
                        ...result.data
                    },
                    errors: {
                        ...countriesResult.errors,
                        ...result.errors
                    }
                };

                if (result.data.products && !skipAutoSelect) {
                    partialResult.data.selectedProductFormData = await dispatch('autoSelectDefaultProduct');
                }

                const has =
                    (state.countries && state.countries.length) ||
                    (state.products && state.products.length) ||
                    (state.formFields && state.formFields.length);

                if (has) {
                    initialDataLoaded = true;
                    persistCheckoutCatalog(state, langIdForCountries, currencyCode);
                    return partialResult;
                }

                if (Object.keys(partialResult.errors).length) {
                    throw createPartialLoadError(partialResult.errors);
                }

                return partialResult;
            };

            initialDataLoadPromise = withLoading(commit, run)
                .finally(() => {
                    initialDataLoadPromise = null;
                });

            return await initialDataLoadPromise;
        },

        async loadCategorizedProducts({ commit, rootGetters }) {
            const currencyCode = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase();
            return await withLoading(commit, async () => await loadParentsCatalogData(commit, {
                currency: currencyCode,
            }));
        },

        /**
         * Refetch GET /products/parents for the active language + currency.
         * Called when the user switches locale or display currency.
         */
        async reloadProductsCatalog({ commit, rootGetters, rootState, state }) {
            const currencyCode = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase();
            const previousProductId = state.formData?.product_id ?? null;

            const products = await withLoading(commit, async () => await loadParentsCatalogData(commit, {
                currency: currencyCode,
                force: true,
            }));

            if (previousProductId != null && Array.isArray(products)) {
                const stillExists = products.some(
                    (product) => String(product?.id ?? '') === String(previousProductId)
                );
                if (!stillExists && products.length > 0) {
                    commit('SET_FORM_DATA', { product_id: products[0].id });
                }
            }

            persistCheckoutCatalog(state, rootState.lang?.id, currencyCode);
            return products;
        },

        /**
         * Refetch /payments/methods/sorted when the user picks a different
         * vehicle-registration country (`plate_country`). Cached by upper-cased
         * plate code so switching back to a previously fetched value is a no-op.
         */
        async reloadPaymentMethods({ commit, state, rootState, rootGetters }) {
            const key = String(state.formData?.plate_country || 'CZ').toUpperCase();
            if (paymentMethodsFetchedForPlateKey === key) return;
            await loadPaymentMethodsData(commit, state.formData?.plate_country);
            const currencyCode = String(rootGetters['currency/currentCurrency']?.code || '').trim().toUpperCase();
            persistCheckoutCatalog(state, rootState.lang?.id, currencyCode);
        },

        async fetchBillingForm({ state, commit }, paymentMethod) {
            const pm = paymentMethod ?? state.formData?.payment_method;
            const key = String(pm || '').toLowerCase().trim();
            if (!PAYMENT_METHODS_NEEDING_BILLING_FORM.includes(key)) {
                commit('SET_BILLING_FORM_FIELDS', []);
                commit('RESET_BILLING_DATA');
                return [];
            }
            try {
                const responseData = await apiRequest(
                    `payments/billing-form?payment_method=${encodeURIComponent(pm)}`,
                    { method: 'GET' }
                );
                if (String(state.formData?.payment_method || '').toLowerCase().trim() !== key) {
                    return [];
                }
                const raw = responseData?.data;
                const fields = Array.isArray(raw) ? [...raw] : [];
                fields.sort((a, b) => (Number(a?.index) || 0) - (Number(b?.index) || 0));

                const prevBilling = state.formData.billing_data && typeof state.formData.billing_data === 'object'
                    ? { ...state.formData.billing_data }
                    : {};

                commit('SET_BILLING_FORM_FIELDS', fields);
                commit('RESET_BILLING_DATA');

                const fieldKeys = fields.map((f) => f?.key).filter(Boolean);
                const restored = {};
                for (const k of fieldKeys) {
                    if (k === 'country') continue;
                    const rawVal = prevBilling[k];
                    if (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '') {
                        restored[k] = rawVal;
                    }
                }
                if (Object.keys(restored).length) {
                    commit('MERGE_BILLING_DATA', restored);
                }

                const plate = String(state.formData?.plate_country || '').trim();
                if (fields.some((f) => f?.key === 'country') && plate) {
                    commit('MERGE_BILLING_DATA', { country: plate });
                }
                return fields;
            } catch {
                commit('SET_BILLING_FORM_FIELDS', []);
                commit('RESET_BILLING_DATA');
                return [];
            }
        },

        async getOrderByToken({ commit }, token) {
            return await withLoading(commit, async () => {
                const responseData = await apiRequest(`/orders/get/${token}`, {
                    method: 'POST',
                });

                commit('SET_ORDER_DATA', responseData);
                return responseData;
            });
        },

        /**
         * Prefill the entire BuyVignette form from a server-stored order draft.
         *
         * Triggered when the user lands on `/buy-vignette?token=<order-token>`
         * (typically a returning customer after a failed/cancelled payment, or
         * a magic-link checkout). Calls POST /orders/request-data/{token} and
         * merges `data.form.main` into formData, resolves the cart product
         * (vehicle_type / category_id / selected_duration) via the existing
         * /products/parents catalog, applies `currency` and `payment_method`,
         * and persists everything to localStorage so a refresh stays in sync.
         */
        async prefillFromOrderToken({ commit, state, dispatch, rootState }, orderToken) {
            const token = String(orderToken || '').trim();
            if (!token) return null;

            return withLoading(commit, async () => {
                let responseData;
                try {
                    responseData = await apiRequest(
                        `/orders/request-data/${encodeURIComponent(token)}`,
                        { method: 'POST' }
                    );
                } catch (e) {
                    return null;
                }

                if (!responseData || (responseData.status && responseData.status !== 200)) {
                    return null;
                }

                const payload = responseData.data || {};
                const main = (payload.form && payload.form.main) || {};

                const allowedKeys = new Set(Object.keys(getDefaultFormData()));
                const patch = {};
                for (const [key, value] of Object.entries(main)) {
                    if (!allowedKeys.has(key)) continue;
                    if (DISALLOWED_FORM_KEYS.has(key)) continue;
                    patch[key] = value;
                }
                if (payload.payment_method) {
                    patch.payment_method = payload.payment_method;
                }

                const firstProduct = Array.isArray(payload.products) ? payload.products[0] : null;
                const productIdRaw = firstProduct?.id ?? null;
                if (productIdRaw != null) {
                    const numericId = Number(productIdRaw);
                    const catalogProduct = (state.products || []).find(
                        (p) => p.id === numericId || String(p.id) === String(productIdRaw)
                    );
                    if (catalogProduct) {
                        const vehicleType = getCategoryDescription(catalogProduct) || patch.vehicle_type || '1-day';
                        const durationMatch = catalogProduct.path?.match(/(\d+)-day/);
                        const duration = durationMatch
                            ? parseInt(durationMatch[1], 10)
                            : (catalogProduct.duration ?? null);
                        const catId = getCategoryId(catalogProduct);
                        patch.product_id = catalogProduct.id;
                        patch.vehicle_type = vehicleType;
                        patch.selected_duration = duration;
                        if (catId != null && catId !== '') patch.category_id = catId;
                    } else {
                        patch.product_id = productIdRaw;
                    }
                }

                if (Object.keys(patch).length) {
                    commit('SET_FORM_DATA', patch);
                }

                /*
                 * Currency switch and payment-methods refetch are independent —
                 * run them in parallel to shave one round-trip off the prefill.
                 * `reloadPaymentMethods` is itself cached per plate-country key,
                 * so it's a no-op when the order's country matches the one
                 * `loadInitialData` already fetched methods for.
                 */
                const sideEffects = [];
                if (payload.currency) {
                    sideEffects.push(
                        dispatch('currency/setCurrencyByCode', {
                            code: payload.currency,
                            skipCatalogRefresh: true,
                        }, { root: true })
                            .catch(() => {})
                    );
                }
                if (patch.plate_country) {
                    sideEffects.push(dispatch('reloadPaymentMethods').catch(() => {}));
                }
                if (sideEffects.length) {
                    await Promise.all(sideEffects);
                }

                await dispatch('saveDataToLocalStorage');

                return { patch, raw: payload };
            });
        },

    },

    getters: {
        formData: state => state.formData,
        step: state => state.step,
        stepName: state => STEP_NAMES[state.step - 1] || 'select-vignette',
        countries: state => state.countries,
        products: state => state.products,
        categorizedProducts: state => state.categorizedProducts || [],
        productIconItems: state => state.productIconItems || [],
        formFields: state => state.formFields,
        paymentMethods: state => [
            ...(state.paymentMethodsMain || []),
            ...(state.paymentMethodsOther || [])
        ],
        paymentMethodsMain: state => state.paymentMethodsMain || [],
        paymentMethodsOther: state => state.paymentMethodsOther || [],
        billingFormFields: state => state.billingFormFields || [],
        paymentMethodFee: state => state.paymentMethodFee,
        loading: state => state.loadingCount > 0,
        orderData: state => state.orderData,
        pendingValidationErrors: state => state.pendingValidationErrors || {},
        /**
         * Maps each form field key to the wizard step (1, 2 or 3) that renders it,
         * so a step can decide whether a validation error belongs to it. Server-driven
         * formFields take precedence over the static STEP_FIELDS defaults.
         */
        validationFieldStepMap: (state) => {
            const map = {};
            Object.entries(STEP_FIELDS).forEach(([stepKey, keys]) => {
                const n = Number(STEP_TO_API_NUMBER[stepKey]);
                (keys || []).forEach((key) => {
                    if (key && !(key in map)) map[key] = n;
                });
            });
            (state.formFields || []).forEach((field) => {
                const n = Number(field?.step);
                if (field?.key && Number.isFinite(n) && n >= 1) {
                    map[field.key] = n;
                }
            });
            return map;
        }
    },
};
